import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/ui/dashboard-layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ProjectDialog } from '@/components/projects/project-dialog';
import { useProjects, useDeleteProject } from '@/hooks/use-projects';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/stores/auth-store';
import { useDebounce } from '@/hooks/use-debounce';
import { SkeletonCard } from '@/components/ui/skeleton';
import { FolderKanban, Plus, Search, Trash2 } from 'lucide-react';
import { EmptyState } from '@/components/ui/empty-state';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { cn, formatDate } from '@/lib/utils';
import { toast } from '@/hooks/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const stageColors: Record<string, string> = {
  enquiry: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
  quote: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  design: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
  develop: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
  review: 'bg-pink-500/10 text-pink-400 border-pink-500/20',
  launch: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20',
  finalised: 'bg-dyndev-green/10 text-dyndev-green border-dyndev-green/20',
};

export function ProjectsPage() {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const isClient = user?.role === 'client';
  const isAdmin = user?.role === 'admin';

  useDocumentTitle('Projects');

  const debouncedSearch = useDebounce(search, 300);
  const { data, isLoading, isError, refetch } = useProjects(debouncedSearch ? { search: debouncedSearch } : undefined);
  const deleteProject = useDeleteProject();
  const [projectToDelete, setProjectToDelete] = useState<any>(null);
  const projects = data?.data || [];

  const handleDeleteProject = async () => {
    if (!projectToDelete) return;
    try {
      const result = await deleteProject.mutateAsync(projectToDelete.id);
      toast({
        title: 'Project deleted',
        description: result?.message || `"${projectToDelete.name}" has been deleted.`,
        variant: 'success',
      });
      setProjectToDelete(null);
    } catch (err: any) {
      toast({
        title: 'Failed to delete project',
        description: err?.message || 'An unexpected error occurred.',
        variant: 'destructive',
      });
    }
  };

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold">Projects</h1>
          <p className="text-sm text-[var(--text-secondary)] mt-1">
            {isClient
              ? 'View and track your ongoing projects.'
              : 'Manage all your client projects.'}
          </p>
        </div>
        {!isClient && (
          <Button onClick={() => setDialogOpen(true)} className="flex items-center gap-2">
            <Plus size={16} />
            New Project
          </Button>
        )}
      </div>

      {/* Search */}
      <div className="relative mb-6 max-w-md">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" />
        <Input
          placeholder="Search projects..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
          aria-label="Search projects"
        />
      </div>

      {/* Error state */}
      {isError && (
        <div className="flex flex-col items-center justify-center h-64 text-center">
          <p className="text-red-400 mb-4">Failed to load projects.</p>
          <button onClick={() => refetch()} className="btn-dyndev text-sm">
            Try Again
          </button>
        </div>
      )}

      {/* Loading state */}
      {isLoading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !isError && projects.length === 0 && (
        <div className="h-64 flex items-center justify-center">
          <EmptyState
            icon={FolderKanban}
            title="No projects yet"
            description={search ? 'No projects match your search.' : isClient ? 'No projects available.' : 'Get started by creating your first project.'}
            action={!isClient && !search ? { label: 'Create Project', onClick: () => setDialogOpen(true) } : undefined}
          />
        </div>
      )}

      {/* Project grid */}
      {!isLoading && projects.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {projects.map((project: any) => (
            <Card
              key={project.id}
              className="cursor-pointer hover:border-dyndev-green/30 hover:shadow-md transition-all duration-200 group"
              onClick={() => navigate(`/projects/${project.id}`)}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-2">
                  <CardTitle className="text-base group-hover:text-dyndev-green transition-colors">
                    {project.name}
                  </CardTitle>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    {isAdmin && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setProjectToDelete(project);
                        }}
                        className="text-[var(--text-secondary)] hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100"
                        title="Delete project"
                        aria-label={`Delete project ${project.name}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                    <Badge
                      variant="outline"
                      className={cn('text-[10px]', stageColors[project.stage] || '')}
                    >
                      {project.stage}
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-[var(--text-secondary)] line-clamp-2 mb-3">
                  {project.description || 'No description'}
                </p>
                <div className="flex items-center justify-between text-xs text-[var(--text-secondary)]">
                  <span>{project.client?.companyName || project.client?.user?.name || 'Unknown client'}</span>
                  <div className="flex items-center gap-3">
                    {isClient && project.projectManager?.name && (
                      <span className="text-dyndev-green">PM: {project.projectManager.name}</span>
                    )}
                    <span>{project._count?.tasks || 0} tasks</span>
                    <span>{project.startDate ? formatDate(project.startDate) : ''}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Project dialog — staff only (New Project button is hidden for clients) */}
      {!isClient && (
        <ProjectDialog open={dialogOpen} onOpenChange={setDialogOpen} />
      )}

      {/* Delete confirmation — admin only */}
      <AlertDialog open={!!projectToDelete} onOpenChange={(open) => { if (!open) setProjectToDelete(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete project?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{projectToDelete?.name}"?
              This will permanently remove the project, its tasks, files, tickets, milestones, comments and site monitor.
              Any invoices linked to this project will also be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteProject}
              disabled={deleteProject.isPending}
              className="bg-red-500 hover:bg-red-600"
            >
              {deleteProject.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
